import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import MaintenanceOverlay from './components/common/MaintenanceOverlay';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import PlannerPage from './pages/PlannerPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import CategoriesPage from './pages/CategoriesPage';
import CompanyPage from './pages/CompanyPage';
import SecurityPage from './pages/SecurityPage';
import MapPage from './pages/MapPage';
import PrintFormPage from './pages/PrintFormPage';
import DayPlanPage from './pages/DayPlanPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import QuotePrintPage from './pages/QuotePrintPage';
import ProformaPrintPage from './pages/ProformaPrintPage';
import TeamPage from './pages/TeamPage';
import SetupAccountPage from './pages/SetupAccountPage';
import ActivityPage from './pages/ActivityPage';

const Private = ({ children }) => {
    const { isLoggedIn } = useAuth();
    return isLoggedIn ? children : <Navigate to="/login" replace />;
};

/** Admin-only pages (quotes, team, activity log); employees are sent to their planner (the API refuses them anyway). */
const AdminOnly = ({ children }) => {
    const { isLoggedIn, isAdmin } = useAuth();
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    return isAdmin ? children : <Navigate to="/planner" replace />;
};

/** Start page: the dashboard for admins, the planner for employees. */
const Home = () => {
    const { isLoggedIn, isAdmin } = useAuth();
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    return <Navigate to={isAdmin ? '/dashboard' : '/planner'} replace />;
};

const PublicOnly = ({ children }) => {
    const { isLoggedIn } = useAuth();
    return isLoggedIn ? <Home /> : children;
};

export default function App() {
    return (
        <BrowserRouter>
            <MaintenanceOverlay />
            <ErrorBoundary>
            <Routes>
                <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
                {/* Invite link; works signed in or out (accepting replaces any current session). */}
                <Route path="/setup-account" element={<SetupAccountPage />} />
                <Route path="/dashboard" element={<Private><DashboardPage /></Private>} />
                <Route path="/jobs" element={<Private><JobsPage /></Private>} />
                <Route path="/jobs/:id" element={<Private><JobDetailPage /></Private>} />
                <Route path="/jobs/:id/print/visit/:visitId" element={<Private><PrintFormPage kind="visit" /></Private>} />
                <Route path="/jobs/:id/print/receipt" element={<Private><PrintFormPage kind="receipt" /></Private>} />
                <Route path="/jobs/:id/proformas/:proformaId/print" element={<AdminOnly><ProformaPrintPage /></AdminOnly>} />
                <Route path="/planner" element={<Private><PlannerPage /></Private>} />
                <Route path="/planner/day" element={<Private><DayPlanPage /></Private>} />
                <Route path="/map" element={<Private><MapPage /></Private>} />
                <Route path="/quotes" element={<AdminOnly><QuotesPage /></AdminOnly>} />
                <Route path="/quotes/:id" element={<AdminOnly><QuoteDetailPage /></AdminOnly>} />
                <Route path="/quotes/:id/print" element={<AdminOnly><QuotePrintPage /></AdminOnly>} />
                <Route path="/clients" element={<Private><ClientsPage /></Private>} />
                <Route path="/clients/:id" element={<Private><ClientDetailPage /></Private>} />
                <Route path="/activity" element={<AdminOnly><ActivityPage /></AdminOnly>} />
                <Route path="/settings/team" element={<AdminOnly><TeamPage /></AdminOnly>} />
                <Route path="/settings/categories" element={<Private><CategoriesPage /></Private>} />
                <Route path="/settings/company" element={<Private><CompanyPage /></Private>} />
                <Route path="/settings/security" element={<Private><SecurityPage /></Private>} />
                <Route path="*" element={<Home />} />
            </Routes>
            </ErrorBoundary>
        </BrowserRouter>
    );
}
